import { useCallback, useEffect, useRef, useState } from 'react';

export function useSearchInput({
  searchValue,
  onSearch,
}: {
  searchValue: string;
  onSearch?: (q: string) => void;
}) {
  const [searchTerm, setSearchTerm] = useState(searchValue);
  const [isSearchExpanded, setIsSearchExpanded] = useState(() => !!searchValue);
  const [isPending, setIsPending] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  // Sync internal state when searchValue changes externally
  useEffect(() => {
    setSearchTerm(searchValue);
    setIsSearchExpanded(!!searchValue);
  }, [searchValue]);

  // Debounced search
  useEffect(() => {
    if (!onSearch) {
      setIsPending(false);
      return;
    }
    setIsPending(true);
    const timer = setTimeout(() => {
      onSearch(searchTerm);
      setIsPending(false);
    }, 300);

    // Reset isPending on cleanup so a cancelled/superseded debounce doesn't
    // leave the search bar stuck in the searching state — e.g. when onSearch
    // becomes undefined or the component unmounts mid-debounce.
    return () => {
      clearTimeout(timer);
      setIsPending(false);
    };
  }, [searchTerm, onSearch]);

  const handleSearchChange = useCallback((value: string) => {
    setSearchTerm(value);
  }, []);

  const handleSearchClick = useCallback(() => {
    setIsSearchExpanded(true);
    setTimeout(() => inputRef.current?.focus(), 0);
  }, []);

  const handleSearchBlur = useCallback(() => {
    if (!searchTerm) {
      setIsSearchExpanded(false);
    }
  }, [searchTerm]);

  return {
    searchTerm,
    isSearchExpanded,
    setIsSearchExpanded,
    inputRef,
    handleSearchChange,
    handleSearchClick,
    handleSearchBlur,
    isPending,
  };
}
